import { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  listPersonas,
  listProspects,
  type AudienceSummary,
  type PersonaView,
  type RemoteProspect,
  type RemoteProspectCampaign
} from '../lib/adminClient'
import { LoadError } from '../components/LoadError'
import { Markets } from './prospects/Markets'
import { People } from './prospects/People'
import { Personas } from './prospects/Personas'
import { Campaigns } from './prospects/Campaigns'
import { Settings } from './prospects/Settings'
import { AudiencesTab, ImportPanel } from './prospects/AdLists'
import { FindEmails } from './prospects/FindEmails'
import { SendQueue } from './prospects/SendQueue'

// Prospects: people we want to reach who are not users yet.
//
// Rebuilt 2026-10-31 at the owner's request ("this is pretty confusing ux and
// layout"): one list and one person at a time, the next step and its reason
// at the top of each person, the messages checked as they are typed, the
// facts with their sources, and everything already sent. Personas and
// campaigns have their own tabs; the ad match lists and importing moved to
// the last tab unchanged.
//
// What it will not do: send on LinkedIn (it copies the message and opens the
// profile; you send it), email anyone without a stated source for the
// address, or show an ad-platform user id (no platform gives one).

type Tab = 'markets' | 'people' | 'queue' | 'personas' | 'campaigns' | 'settings' | 'ads'
const TABS: Array<[Tab, string]> = [
  ['markets', 'Markets'],
  ['people', 'People'],
  ['queue', 'Send queue'],
  ['personas', 'Personas'],
  ['campaigns', 'Campaigns'],
  ['settings', 'Signatures & guide'],
  ['ads', 'Ad lists & import']
]

type AiInfo = { model: string; available: boolean; spentUsd: number; findMax: number; emailsMax?: number }

export default function Prospects(): JSX.Element {
  const location = useLocation()
  const navigate = useNavigate()
  // The markets are the way in: pick one, then work the people in it.
  const [tab, setTab] = useState<Tab>('markets')
  // Set when a market is opened from the Markets tab; the People list
  // then shows only that market until it is cleared.
  const [market, setMarket] = useState<string | null>(null)
  const [people, setPeople] = useState<RemoteProspect[] | null>(null)
  const [campaigns, setCampaigns] = useState<RemoteProspectCampaign[]>([])
  const [audiences, setAudiences] = useState<AudienceSummary[]>([])
  const [personas, setPersonas] = useState<PersonaView[]>([])
  const [ai, setAi] = useState<AiInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const selected = location.hash ? decodeURIComponent(location.hash.slice(1)) : null

  const load = useCallback(async () => {
    const [r, pe] = await Promise.all([listProspects({ limit: 500 }), listPersonas()])
    if (!r.ok) return setError(r.error)
    setError(null)
    setPeople(r.data.prospects)
    setCampaigns(r.data.campaigns)
    setAudiences(r.data.audiences)
    if (pe.ok) {
      setPersonas(pe.data.personas)
      setAi(pe.data.ai)
    } else setPersonas(r.data.personas ?? [])
  }, [])
  useEffect(() => void load(), [load])

  const select = (id: string | null): void => {
    navigate({ pathname: '/prospects', hash: id ? `#${encodeURIComponent(id)}` : '' }, { replace: true })
  }
  const openPerson = (id: string): void => {
    setTab('people')
    select(id)
  }
  useEffect(() => {
    if (selected) setTab('people')
  }, [selected])

  const replace = (p: RemoteProspect): void => setPeople((list) => (list ? list.map((x) => (x.id === p.id ? p : x)) : list))
  const todo = people?.filter((p) => p.plan.actionable && p.plan.step !== 'hold' && p.plan.step !== 'done').length ?? 0
  const waitingOnUs = people?.filter((p) => p.plan.step === 'reply').length ?? 0
  const signedUp = people?.filter((p) => p.status === 'converted').length ?? 0

  return (
    <div>
      <div className="mb-5">
        <div className="text-[11px] uppercase tracking-[0.3em] text-slate-400">Growth</div>
        <h1 className="text-2xl font-semibold mt-1">Prospects</h1>
        {people && (
          <div className="text-sm text-slate-400 mt-1" data-testid="prospects-summary">
            {people.length} people · {todo} with a next step · {waitingOnUs} waiting on your reply · {signedUp} signed up
          </div>
        )}
      </div>
      <div className="flex flex-wrap gap-1 mb-4 border-b border-white/5">
        {TABS.map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === t ? 'border-accent text-white' : 'border-transparent text-slate-400 hover:text-white'}`}
            data-testid={`tab-${t}`}
          >
            {label}
          </button>
        ))}
      </div>
      {error && <LoadError what="prospects" endpoint="GET /admin/prospects" error={error} testId="prospects-error" />}
      {!people && !error && <div className="glass-card rounded-xl p-6 text-sm text-slate-400">Loading…</div>}
      {people && people.length === 0 && tab === 'people' && (
        <div className="glass-card rounded-xl p-6 text-sm text-slate-300 mb-4" data-testid="prospects-empty">
          No prospects stored yet. Import a batch on the{' '}
          <button className="text-accent hover:underline" onClick={() => setTab('ads')}>
            Ad lists &amp; import
          </button>{' '}
          tab, or find people from a persona.
        </div>
      )}
      {tab === 'markets' && (
        <Markets
          onOpenMarket={(key) => {
            setMarket(key)
            setTab('people')
          }}
        />
      )}
      {people && people.length > 0 && tab === 'people' && <FindEmails people={people} ai={ai} onDone={() => void load()} />}
      {people && people.length > 0 && tab === 'people' && (
        <People
          people={people}
          market={market}
          onClearMarket={() => setMarket(null)}
          campaigns={campaigns}
          personas={personas}
          selected={selected}
          onSelect={select}
          onChanged={(p) => {
            replace(p)
            void load()
          }}
          onDeleted={(id) => setPeople((list) => (list ? list.filter((x) => x.id !== id) : list))}
        />
      )}
      {people && tab === 'queue' && <SendQueue people={people} onSent={(p) => replace(p)} onOpenPerson={openPerson} />}
      {people && tab === 'personas' && <Personas personas={personas} ai={ai} people={people} onChanged={() => void load()} onOpenPerson={openPerson} />}
      {people && tab === 'campaigns' && <Campaigns campaigns={campaigns} personas={personas} people={people} onChanged={() => void load()} onOpenPerson={openPerson} />}
      {tab === 'settings' && <Settings />}
      {people && tab === 'ads' && (
        <div className="space-y-4">
          <ImportPanel onImported={() => void load()} />
          <AudiencesTab summary={audiences} campaigns={campaigns} />
        </div>
      )}
    </div>
  )
}
